import React, { useState, useCallback, useRef, useEffect, useMemo } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import AppLayout from "@amzn/meridian/app-layout"
import Masthead, { MastheadTitle } from "@amzn/meridian/masthead"
import SideMenu, { SideMenuLink } from "@amzn/meridian/side-menu"
import { SelectOption } from "@amzn/meridian/select"
import SearchableSelect from "src/components/SearchableSelect"
import Button from "@amzn/meridian/button"
import Icon from "@amzn/meridian/icon"
import Text from "@amzn/meridian/text"
import Row from "@amzn/meridian/row"
import Column from "@amzn/meridian/column"
import Popover from "@amzn/meridian/popover"
import homeIconTokens from "@amzn/meridian-tokens/base/icon/home"
import clipboardIconTokens from "@amzn/meridian-tokens/base/icon/clipboard"
import searchIconTokens from "@amzn/meridian-tokens/base/icon/search"
import archiveIconTokens from "@amzn/meridian-tokens/base/icon/archive"
import exportIconTokens from "@amzn/meridian-tokens/base/icon/export-large"
import metricsIconTokens from "@amzn/meridian-tokens/base/icon/metrics"
import creditCardIconTokens from "@amzn/meridian-tokens/base/icon/credit-card"
import printIconTokens from "@amzn/meridian-tokens/base/icon/print"
import chevronDownTokens from "@amzn/meridian-tokens/base/icon/chevron-down-small"
import { useFacility, FACILITY_OPTIONS } from "src/context/FacilityContext"
import { getUserInfo } from "src/services/authService"
import { usePageTitle } from "src/hooks/usePageTitle"
import factoryIconTokens from "src/icons/factoryIcon"
import CommandPalette from "src/components/CommandPalette"
import CataloguePrefetch from "src/components/CataloguePrefetch"

interface AppShellProps {
  children: React.ReactNode
}

/**
 * Visually hides content while keeping it available to screen readers
 * (the standard "visually hidden" / sr-only technique).
 */
const visuallyHidden: React.CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
}

/** WFM brand green — logo wordmark and the user avatar. */
const BRAND_GREEN = "#006F46"

const logoTextStyle: React.CSSProperties = {
  fontWeight: 800,
  letterSpacing: "-0.5px",
  fontSize: "22px",
  color: BRAND_GREEN,
}

const userMenuButtonStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: "4px 8px",
  borderRadius: "4px",
}

const avatarStyle: React.CSSProperties = {
  width: "32px",
  height: "32px",
  borderRadius: "50%",
  backgroundColor: BRAND_GREEN,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff",
  fontWeight: 700,
  fontSize: "14px",
}

/**
 * Application shell with flat sidebar navigation (CulinaryCompass pattern).
 *
 * - type="skinny": collapses to icon-only rail (never fully hidden)
 * - Flat top-level links only (no nesting per Meridian guidelines)
 * - Sub-navigation via page-level tabs within each section
 * - Command Palette (Cmd+K) for power-user navigation
 */
const AppShell: React.FC<AppShellProps> = ({ children }) => {
  const [sideMenuOpen, setSideMenuOpen] = useState(true)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false)
  const { facility, setFacility } = useFacility()
  const navigate = useNavigate()
  const location = useLocation()
  // Memoised: getUserInfo() reads sessionStorage and decodes the JWT, so it
  // should not re-run on every render.
  const userInfo = useMemo(() => getUserInfo(), [])
  const userButtonRef = useRef<HTMLButtonElement>(null)

  // Sync the document title with the route and announce navigations to
  // screen readers (SPAs do neither by default — WCAG 2.4.2).
  const pageTitle = usePageTitle()

  // Global Cmd+K / Ctrl+K shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault()
        setCommandPaletteOpen((prev) => !prev)
      }
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [])

  const onOpenSideMenu = useCallback(() => setSideMenuOpen(true), [])
  const onCloseSideMenu = useCallback(() => setSideMenuOpen(false), [])

  const displayName = userInfo?.name ?? "User"
  const displayEmail = userInfo?.email ?? ""
  const avatarLetter = displayName.charAt(0).toUpperCase()

  const isActive = (basePath: string) => {
    if (basePath === "/") return location.pathname === "/"
    return location.pathname.startsWith(basePath)
  }

  return (
    <AppLayout headerComponent={Masthead} sidebarComponent={SideMenu}>
      <Masthead>
        <MastheadTitle href="/" onClick={(href: string) => navigate(href)}>
          <span style={logoTextStyle}>Eclipse</span>
        </MastheadTitle>
        <Row alignmentVertical="center" spacing="300">
          {/* Command Palette trigger */}
          <Button
            type="icon"
            size="small"
            onClick={() => setCommandPaletteOpen(true)}
            aria-label="Search (Cmd+K)"
          >
            <Icon tokens={searchIconTokens} />
          </Button>

          <SearchableSelect
            value={facility}
            onChange={setFacility}
            size="small"
            width={180}
            aria-label="Select facility"
          >
            {FACILITY_OPTIONS.map((opt) => (
              <SelectOption key={opt.value} value={opt.value} label={opt.label} />
            ))}
          </SearchableSelect>

          {/* User dropdown */}
          <button
            ref={userButtonRef}
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            style={userMenuButtonStyle}
            aria-label="User menu"
            aria-haspopup="menu"
            aria-expanded={userMenuOpen}
          >
            <span style={avatarStyle}>{avatarLetter}</span>
            <Text type="b200">{displayName}</Text>
            <Icon tokens={chevronDownTokens} />
          </button>

          <Popover
            anchorNode={userButtonRef.current ?? undefined}
            open={userMenuOpen}
            onClose={() => setUserMenuOpen(false)}
            position="bottom"
            alignment="end"
          >
            <Column spacing="200" spacingInset="300" width={220}>
              <Text type="b300" tag="strong">{displayName}</Text>
              {displayEmail && <Text type="b200" color="secondary">{displayEmail}</Text>}
            </Column>
          </Popover>
        </Row>
      </Masthead>

      {/* Flat sidebar — icon rail when collapsed */}
      <SideMenu
        type="skinny"
        open={sideMenuOpen}
        onOpen={onOpenSideMenu}
        onClose={onCloseSideMenu}
        width={240}
      >
        <SideMenuLink
          href="/"
          onClick={(href: string) => navigate(href)}
          selected={isActive("/")}
          icon={homeIconTokens}
        >
          Dashboard
        </SideMenuLink>

        <SideMenuLink
          href="/po"
          onClick={(href: string) => navigate(href)}
          selected={isActive("/po") || isActive("/receipts") || isActive("/suppliers") || isActive("/invoices") || isActive("/exceptions")}
          icon={clipboardIconTokens}
        >
          Purchasing
        </SideMenuLink>

        <SideMenuLink
          href="/items/recipes"
          onClick={(href: string) => navigate(href)}
          selected={isActive("/items")}
          icon={searchIconTokens}
        >
          Items & Recipes
        </SideMenuLink>

        <SideMenuLink
          href="/inventory"
          onClick={(href: string) => navigate(href)}
          selected={isActive("/inventory") || isActive("/locations") || isActive("/reorder") || isActive("/pick") || isActive("/quality") || isActive("/trace")}
          icon={archiveIconTokens}
        >
          Inventory
        </SideMenuLink>

        <SideMenuLink
          href="/work-orders"
          onClick={(href: string) => navigate(href)}
          selected={
            isActive("/work-orders") ||
            isActive("/work-order-plans") ||
            isActive("/mrp") ||
            isActive("/production-plan")
          }
          icon={factoryIconTokens}
        >
          Manufacturing
        </SideMenuLink>

        <SideMenuLink
          href="/store-orders"
          onClick={(href: string) => navigate(href)}
          selected={isActive("/store-orders") || isActive("/delivery") || isActive("/shipping")}
          icon={exportIconTokens}
        >
          Fulfillment
        </SideMenuLink>

        <SideMenuLink
          href="/supplier-metrics"
          onClick={(href: string) => navigate(href)}
          selected={isActive("/supplier-metrics") || isActive("/accounting")}
          icon={metricsIconTokens}
        >
          Analytics
        </SideMenuLink>

        <SideMenuLink
          href="/finance"
          onClick={(href: string) => navigate(href)}
          selected={isActive("/finance")}
          icon={creditCardIconTokens}
        >
          Finance
        </SideMenuLink>

        <SideMenuLink
          href="/print-center"
          onClick={(href: string) => navigate(href)}
          selected={isActive("/print-center")}
          icon={printIconTokens}
        >
          Print Center
        </SideMenuLink>

      </SideMenu>

      {children}

      {/* Polite live region: announces the current page to screen readers on
          client-side navigation, which the SPA router does not do on its own. */}
      <div aria-live="polite" role="status" style={visuallyHidden}>
        {pageTitle}
      </div>

      <CommandPalette open={commandPaletteOpen} onClose={() => setCommandPaletteOpen(false)} />

      {/* Warm supplier + item catalogues so dropdowns don't flash empty on first open. */}
      <CataloguePrefetch />
    </AppLayout>
  )
}

export default AppShell
